import ReactEcharts from 'echarts-for-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { BasicTable, ContentCard } from '@/components/customComponents'

import {
  barChartOptions,
  lineChartOptions,
  pieChartOptions,
} from '@/constants/CHART_CONSTANTS'

import { toast, useCompliance } from '@/hooks'
import {
  selectPageRefreshTrigger,
  useAppSelector,
  useReduxBaseFilter,
} from '@/store'
import { dateToString } from '@/utils'

interface ComplianceTabProps {
  itemName: string
}

export const ComplianceTab = ({ itemName }: ComplianceTabProps) => {
  const { t } = useTranslation()
  const { selectedBranch, date } = useReduxBaseFilter('spend-lens-item')
  const refreshTrigger = useAppSelector(
    selectPageRefreshTrigger('spend-lens-item')
  )

  const {
    compliancePercentageChartData,
    monthlyComplianceChartData,
    storeLeakageChartData,
    storeComplianceDetails,
    storeComplianceDetailsColumns,
    loading,
    error,
    refetch,
  } = useCompliance(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  // Refetch data when refresh trigger is triggered
  useEffect(() => {
    if (refreshTrigger) {
      console.log('Refreshing compliance data...')
      refetch()
    }
  }, [refreshTrigger, refetch])

  // Show error toast if there is an error
  useEffect(() => {
    if (error) {
      toast({
        title: t('common.errors.serverError'),
        description: error?.message,
        variant: 'destructive',
      })
    }
  }, [error])

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ContentCard
          title={t('clarityLens.spendLens.charts.compliancePercent')}
          loading={loading}
          isEmpty={!compliancePercentageChartData}
        >
          {compliancePercentageChartData && (
            <ReactEcharts
              option={getPieChartData(
                compliancePercentageChartData.data,
                compliancePercentageChartData.labels
              )}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.spendLens.charts.seasonalCompliancePercentage')}
          loading={loading}
          isEmpty={!monthlyComplianceChartData}
        >
          {monthlyComplianceChartData && (
            <ReactEcharts
              option={lineChartOptions(
                monthlyComplianceChartData.rawData,
                monthlyComplianceChartData.seriesNames,
                monthlyComplianceChartData.xAxisNames,
                'percentage'
              )}
            />
          )}
        </ContentCard>
        <ContentCard
          title={t('clarityLens.spendLens.charts.complianceByStoresLeakage')}
          loading={loading}
          isEmpty={!storeLeakageChartData || storeLeakageChartData.length === 0}
        >
          {storeLeakageChartData && storeLeakageChartData.length > 0 && (
            <ReactEcharts option={getBarChartData(storeLeakageChartData)} />
          )}
        </ContentCard>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
        <BasicTable
          title={t('clarityLens.spendLens.charts.storeComplianceDetails')}
          data={storeComplianceDetails}
          columns={storeComplianceDetailsColumns}
          loading={loading}
          enablePagination
          itemsPerPage={5}
        />
      </div>
    </div>
  )
}

const getPieChartData = (dataObj: Record<string, number>, labels: string[]) => {
  const data = labels.map((label) => dataObj[label] ?? 0)
  return pieChartOptions(data, labels, 'percentage')
}

const getBarChartData = (storeData: { name: string; value: number }[]) => {
  const rawData = [storeData.map((item) => item.value)]
  const seriesNames = ['Leakage']
  const xAxisNames = storeData.map((item) => item.name)

  return barChartOptions(rawData, seriesNames, xAxisNames, 'money')
}
